import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { checkIsObjectEmpty } from "@my-scoot/exly-react-component-lib";
import { useFetchCustomCodeComboPage } from "ui/pages/CustomCodes/hooks/useFetchCustomCodeComboPage";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { api, dataProvider } from "data";
import { useRefresh } from "react-admin";
import { logError } from "utils/error";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { isValidEmail, is_empty } from "utils/validations";
import { getSegmentList } from "../customers/Segments/components/segmentHelper";
import constants from "constants/constants";
import useRecipients from "../communications/CustomEmail/ComposeEmail/useRecipients";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import {
  discount_form_keys,
  DISCOUNT_PAYMENT_PLAN_TYPE,
} from "features/Discount/constants/Discount.constants";
import { apiMethods } from "data/api.constants";
import { EDIT_DISCOUNT_CODE_API } from "features/Discount/constants/Discount.apis";
import { isRequestSuccessful } from "utils/Utils";
import { LTO_SUPPORTED_CURRENCIES } from "ui/modules/LimitedOfferModalContent/constants";
import { useInfiniteListingOptions } from "features/Listings/utils/useInfiniteListingOptions";
import useDebounce from "hooks/useDebounce";
import { DEBOUNCE_DELAY } from "features/Account/modules/PaymentOptions/modules/InternationalPayment/InternationalPayment.constants";
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE_50,
} from "features/Listings/Listings.constants";

const useCreateDiscounts = (props) => {
  const { recipients, recipientsLoading, fetchRecipients } = useRecipients();
  const isEdit = !checkIsObjectEmpty(props?.selectedRecordData);

  const [segmentsData, setSegmentsData] = useState([]);
  const [errorString, setErrorString] = useState("");

  const { onClose, listingMultiSelect, setSelectedDiscountCode } = props;

  // Combo pages hook
  const {
    data: comboPagesOptions,
    loading: comboPagesLoader,
    lastElementRef: lastElementComboPagesRef,
  } = useFetchCustomCodeComboPage({
    initialData: { isActive: true, currencies: LTO_SUPPORTED_CURRENCIES },
  });

  const couponApplicable = {
    everyone: 1,
    specificListings: 2,
    loyaltyUsers: 4,
    specificCombos: 5,
  };

  const CustomerEligibility = {
    everyone: 1,
    specificGroups: 2,
  };

  let form_fields = {
    code: "Discount Code",
    percentage: "Discount Value",
    usage: "Usage Limit",
    save_btn: "Create",
    absolute_type: 2,
    percentage_type: 1,
  };

  const { pushNotification, notify } = useNotifications();
  const refresh = useRefresh();

  // ---------------- new-code
  const {
    value: searchQueryInput,
    setValue: setSearchQueryInput,
    debouncedValue: searchQuery,
  } = useDebounce({ inititalValue: "", delay: DEBOUNCE_DELAY });
  const {
    options: infiniteOptions,
    loading: offeringsLoading,
    lastElementRef: offeringsLastElementRef,
  } = useInfiniteListingOptions({
    listingApiPayload: searchQuery ? { title__icontains: searchQuery } : {},
  });
  const [audienceList, setAudienceList] = useState({});
  const [loading, setLoading] = useState(false);
  const [audienceSearchQuery, setAudienceSearchQuery] = useState("");
  const [audiencePage, setAudiencePage] = useState(DEFAULT_PAGE_NUMBER);

  useEffect(() => {
    setAudiencePage(DEFAULT_PAGE_NUMBER);
  }, [audienceSearchQuery]);

  const addDiscountCoupon = async (
    formState,
    showOfferingVariationsDropdown
  ) => {
    try {
      let params = {
        discount: {
          code: formState.code,
          value: parseFloat(formState.value),
          usage_limit: parseInt(formState.usage_limit) || 0,
          type: formState.type,
          applicability: Number(formState.applicability),
          include_gst: formState.include_gst,
          currency: formState.currency,
          [discount_form_keys.discount_application_scope]:
            formState[discount_form_keys.discount_application_scope],
          ...(!is_empty(
            formState[discount_form_keys.instalment_distribution_mechanism]
          ) && {
            [discount_form_keys.instalment_distribution_mechanism]:
              formState[discount_form_keys.instalment_distribution_mechanism],
          }),
        },
      };

      if (showOfferingVariationsDropdown) {
        params.offering_variant_uuids = formState.offering_variant_uuids;
      }

      if (!is_empty(formState[discount_form_keys.dynamic_link_uuids])) {
        params[discount_form_keys.dynamic_link_uuids] =
          formState[discount_form_keys.dynamic_link_uuids];
      }

      const discountScope =
        formState[discount_form_keys.discount_application_scope];
      const isCustomPaymentLinksOnly =
        Array.isArray(discountScope) &&
        discountScope.length === 1 &&
        discountScope[0] === DISCOUNT_PAYMENT_PLAN_TYPE.CUSTOM_PAYMENT_LINKS;

      if (
        formState.listing_uuids.length > 0 &&
        !(
          isCustomPaymentLinksOnly &&
          !is_empty(formState?.[discount_form_keys.dynamic_link_uuids])
        )
      ) {
        if (typeof formState.listing_uuids === "string") {
          params["listing_uuids"] = [formState.listing_uuids];
        } else {
          params["listing_uuids"] = formState.listing_uuids;
        }
      }

      if (formState.type === form_fields.absolute_type) {
        Object.assign(params, { currency: formState.currency });
      }

      if (formState.applicability == couponApplicable.loyaltyUsers) {
        Object.assign(params, {
          audience: {
            ...(!checkIsArrayEmpty(formState.audience.segment) && {
              segment: formState.audience.segment[0].value,
            }),
            ...(!checkIsArrayEmpty(formState.audience.booked_listing_uids) && {
              booked_listing_uids: formState.audience.booked_listing_uids.map(
                (item) => item.value
              ),
            }),
            ...(!checkIsArrayEmpty(formState.audience.groups) && {
              groups: formState.audience.groups,
            }),
            ...(!checkIsArrayEmpty(formState.audience.extras) && {
              extras: formState.audience.extras,
            }),
          },
        });
      }

      // Add combo_pages if specific combos applicability is selected
      if (formState.applicability == couponApplicable.specificCombos) {
        Object.assign(params, {
          combo_pages: formState.combo_pages,
        });
      }

      const data = await dataProvider.custom_request(
        api.create_discount,
        "POST",
        params
      );

      if (data.status === 200) {
        pushNotification(data.message, {
          variant: "outlined",
          color: "primary",
        });
        if (listingMultiSelect) {
          onClose();
          if (!formState.is_promotional_coupon) {
            refresh();
            window.location.reload();
          } else if (setSelectedDiscountCode) {
            setSelectedDiscountCode({
              ...formState,
              discount_uid: data.data?.discount_uid,
            });
          }
        } else {
          return {
            listingId: formState.listing_uuids,
            couponCode: params.discount.code,
          };
        }
      }
    } catch (error) {
      logError({
        error: error,
        occuredAt:
          "addDiscountCoupon in src/ui/pages/discounts/useCreateDiscounts.js",
        when: "Error while add discount coupon",
      });
      notify(
        error?.body?.message ?? SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
    }
  };

  const editDiscountCoupon = async ({ formState, discount_uid }) => {
    try {
      const payload = {
        discount_uid,
        updated_values: {
          ...(!is_empty(formState[discount_form_keys.expiry]) && {
            expiry: formState[discount_form_keys.expiry],
          }),
          usage_limit: parseInt(formState.usage_limit) || 0,
        },
      };
      const response = await dataProvider.custom_request(
        EDIT_DISCOUNT_CODE_API,
        apiMethods.POST,
        payload
      );

      if (isRequestSuccessful(response.status)) {
        notify(response.message, notification_color_keys.success);
        refresh();
        onClose();
      }
    } catch (error) {
      logError({
        error: error,
        occuredAt:
          "editDiscountCoupon in src/ui/pages/discounts/useCreateDiscounts.js",
        when: "Error while add discount coupon",
      });
      notify(
        error?.body?.message ?? SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
    }
  };

  const handleDeleteOption = (option, form) => {
    const { groupBy, key } = option;
    let data = form.getState().values.audience;
    let updatedState;

    if (groupBy === "Recipient Groups (Click on group to add)") {
      let updatedGroup = data.groups;
      updatedGroup = updatedGroup.filter((item) => item !== key);
      updatedState = { ...data, groups: [...updatedGroup] };
    } else if (groupBy === "Recipients") {
      let updatedExtras = data.extras;
      updatedExtras = updatedExtras.filter((item) => item !== key);
      updatedState = { ...data, extras: [...updatedExtras] };
    } else if (option.isSegment) {
      let updatedSegments = data.segment;
      updatedSegments = updatedSegments.filter(
        (item) => item.value !== option.value
      );
      updatedState = { ...data, segment: [...updatedSegments] };
    } else if (option.isListing) {
      let updatedListing = data.booked_listing_uids;
      updatedListing = updatedListing.filter(
        (item) => item.value !== option.value
      );
      updatedState = { ...data, booked_listing_uids: [...updatedListing] };
    }

    form.change("audience", updatedState);
  };

  const getSpecificGroupsOrCustomersOptions = (filterQuery) => {
    const query = filterQuery ? filterQuery.toLowerCase() : "";

    const groups = audienceList.groups
      ? Object.keys(audienceList.groups[0])
          .map((key) => ({
            groupBy: "Recipient Groups (Click on group to add)",
            key: audienceList.groups[0][key],
            isRecipientGroup: true,
          }))
          .filter((item) => item.key && item.key.toLowerCase().includes(query))
      : [];

    const segments = segmentsData
      ? segmentsData
          .map((item) => ({
            groupBy: "Segments",
            key: `${item.label} (${item.count})`,
            isSegment: true,
            value: item.value,
          }))
          .filter((item) => item.key && item.key.toLowerCase().includes(query))
      : [];

    const offeringCustomers = recipients
      ? recipients
          .filter((item) => "listing_type" in item)
          .map((item) => ({
            ...item,
            groupBy: "Offering customers",
            key: `${item.display_name} (${item.user_count})`,
            value: item.key,
            isListing: true,
          }))
          .filter((item) => item.key && item.key.toLowerCase().includes(query))
      : [];

    const customers = audienceList?.customers
      ? audienceList.customers
          .filter((customer) => {
            return customer && customer.toLowerCase().includes(query);
          })
          .map((customer) => ({
            groupBy: "Recipients",
            key: customer,
            isRecipient: true,
          }))
      : [];

    const options = [
      ...groups,
      ...segments,
      ...offeringCustomers,
      ...customers,
    ];

    if (isValidEmail(filterQuery)) {
      if (!options.some((opt) => opt.key === filterQuery)) {
        options.unshift({
          key: filterQuery,
          groupBy: "Recipients",
          isRecipient: true,
        });
      }
    }
    return options;
  };

  const allFilteredAudienceOptions = useMemo(() => {
    return getSpecificGroupsOrCustomersOptions(audienceSearchQuery);
  }, [audienceList, segmentsData, recipients, audienceSearchQuery]);

  const audienceOptions = useMemo(() => {
    return allFilteredAudienceOptions.slice(
      0,
      audiencePage * DEFAULT_PAGE_SIZE_50
    );
  }, [allFilteredAudienceOptions, audiencePage]);

  const hasMoreAudience =
    audienceOptions.length < allFilteredAudienceOptions.length;

  const observerRef = useRef(null);
  const audienceLastElementRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (node) {
        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasMoreAudience) {
            setAudiencePage((prev) => prev + 1);
          }
        });
        observerRef.current.observe(node);
      }
    },
    [hasMoreAudience]
  );

  const resetAudienceFilter = useCallback(() => {
    setAudienceSearchQuery("");
    setAudiencePage(DEFAULT_PAGE_NUMBER);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        fetchRecipients({ page: "all", all_listings: true });

        // get audience list
        const audienceResponse = await dataProvider.custom_request(
          api.get_audiance_list,
          "GET"
        );

        if (
          audienceResponse.status === 200 &&
          !is_empty(audienceResponse.data)
        ) {
          const audienceData = audienceResponse.data.audiences;
          setAudienceList(audienceData);

          const showCount = true;
          const segmentListDataCustomers = await getSegmentList(
            showCount,
            constants.segments_target_type.customers
          );
          const segmentListDataLeads = await getSegmentList(
            showCount,
            constants.segments_target_type.leads
          );
          const segmentListData =
            segmentListDataCustomers.concat(segmentListDataLeads);

          setSegmentsData(segmentListData);
        }
      } catch (error) {
        logError({ error });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const mergeEditListings = (list) => {
    if (!isEdit || !props?.selectedRecordData?.listings) {
      return list;
    }

    const editListings = props.selectedRecordData.listings.map((listing) => ({
      ...listing,
      label: listing.title,
      value: listing.uuid,
    }));

    const existingUuids = new Set(list.map((item) => item.uuid));

    return [
      ...editListings.filter((item) => !existingUuids.has(item.uuid)),
      ...list,
    ];
  };

  const offeringsOptions = useMemo(() => {
    const list = (infiniteOptions || []).map((listing) => ({
      ...listing,
      label: listing.title,
      value: listing.uuid,
    }));

    return mergeEditListings(list);
  }, [infiniteOptions, isEdit, props?.selectedRecordData?.listings]);

  const customerEligibilityRadioOptions = [
    { label: "Everyone", value: CustomerEligibility.everyone },
    {
      label: "Specific groups or customers",
      value: CustomerEligibility.specificGroups,
    },
  ];

  /**
   *  format group adience key to be sent, as backend expects value as 
   ["all_leads", "all_customers"], but currently it is 
   ["All Leads(161)", "All Customers(127)"]
   */
  const formatAudienceKey = (str) => {
    return str
      .toLowerCase()
      .replace(/\s/g, "_") // to replace space with underscore
      .replace(/[^a-z_]/g, ""); // remove any character other than a-z and "_"
  };

  const onChangeAudience = ({ value, form, formValues }) => {
    if (is_empty(value)) {
      form.change("audience", {
        groups: [],
        extras: [],
        booked_listing_uids: [],
        segment: [],
      });
      return;
    }

    const newValue = value[value.length - 1];

    if (newValue.key === "cta") return;

    // if already selected, deselect
    if (
      formValues.audience.groups.includes(newValue.key) ||
      formValues.audience.groups.includes(formatAudienceKey(newValue.key)) ||
      formValues.audience.extras.includes(newValue.key) ||
      formValues.audience.booked_listing_uids.find(
        (item) => item.value === newValue.value
      ) ||
      formValues.audience.segment.find((item) => item.value === newValue.value)
    ) {
      if (newValue.isRecipientGroup) {
        let updatedGroup = formValues.audience.groups;
        updatedGroup = updatedGroup.filter(
          (item) => item !== formatAudienceKey(newValue.key)
        );
        form.change("audience", {
          ...formValues.audience,
          groups: [...updatedGroup],
        });
      } else if (newValue.isRecipient) {
        let updatedExtras = formValues.audience.extras;
        updatedExtras = updatedExtras.filter((item) => item !== newValue.key);
        form.change("audience", {
          ...formValues.audience,
          extras: [...updatedExtras],
        });
      } else if (newValue.isListing) {
        let updatedOffering = formValues.audience.booked_listing_uids;
        updatedOffering = updatedOffering.filter(
          (item) => item.value !== newValue.value
        );
        form.change("audience", {
          ...formValues.audience,
          booked_listing_uids: [...updatedOffering],
        });
      } else if (newValue.isSegment) {
        let updatedSegment = formValues.audience.segment;
        updatedSegment = updatedSegment.filter(
          (item) => item.value !== newValue.value
        );
        form.change("audience", {
          ...formValues.audience,
          segment: [...updatedSegment],
        });
      }
    }
    // else add if not already selected
    else if (newValue.isRecipientGroup) {
      form.change("audience", {
        ...formValues.audience,
        groups: [
          ...formValues.audience.groups,
          formatAudienceKey(newValue.key),
        ],
      });
    } else if (newValue.isRecipient) {
      form.change("audience", {
        ...formValues.audience,
        extras: [...formValues.audience.extras, newValue.key],
      });
    } else if (newValue.isListing) {
      form.change("audience", {
        ...formValues.audience,
        booked_listing_uids: [
          ...formValues.audience.booked_listing_uids,
          newValue,
        ],
      });
    } else if (newValue.isSegment) {
      form.change("audience", {
        ...formValues.audience,
        segment: [newValue], // only one segment selected at once
      });
    }
  };

  const onCustomerEligibilityChange = ({ value, form, formValues }) => {
    if (value === CustomerEligibility.everyone) {
      form.change("audience", {
        ...formValues.audience,
        groups: ["all"],
        extras: [],
      });

      form.change("customerEligibilityType", CustomerEligibility.everyone);
    }

    if (value === CustomerEligibility.specificGroups) {
      form.change(
        "customerEligibilityType",
        CustomerEligibility.specificGroups
      );

      form.change("audience", {
        ...formValues.audience,
        groups: [],
        extras: [],
        booked_listing_uids: [],
        segment: [],
      });
    }
  };

  const getComboPagesOptions = isEdit
    ? (props?.selectedRecordData?.combos || []).map((e) => ({
        label: e?.title,
        value: e?.dynamic_link_id,
        ...e,
      }))
    : comboPagesOptions;

  return {
    getSpecificGroupsOrCustomersOptions,
    audienceOptions,
    audienceLastElementRef,
    audienceSearchQuery,
    setAudienceSearchQuery,
    resetAudienceFilter,
    offeringsOptions,
    infiniteOptions,
    offeringsLoading,
    offeringsLastElementRef,
    searchQuery: searchQueryInput,
    setSearchQuery: setSearchQueryInput,
    debouncedSearchQuery: searchQuery,
    couponApplicable,
    CustomerEligibility,
    addDiscountCoupon,
    handleDeleteOption,
    form_fields,
    loading,
    customerEligibilityRadioOptions,
    segmentsData,
    recipientsLoading,
    formatAudienceKey,
    onChangeAudience,
    onCustomerEligibilityChange,
    setErrorString,
    errorString,
    editDiscountCoupon,
    comboPagesOptions: getComboPagesOptions,
    comboPagesLoader,
    lastElementComboPagesRef,
  };
};

export default useCreateDiscounts;
